import { useEffect } from "react";
import { CalendarOff, BriefcaseBusiness, X, Lock } from "lucide-react";

export const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

const pad = (n) => String(n).padStart(2, "0");

function MonthGrid({ year, month, entries, onDayClick, todayStr }) {
  const first = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`month-${month + 1}`}>
      <div className="text-xs font-semibold text-slate-900 mb-2 px-0.5">{MONTHS[month]}</div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {WEEKDAYS.map((w, i) => (
          <div key={i} className="text-[9px] font-semibold text-slate-400 pb-0.5">{w}</div>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} />;
          const dateStr = `${year}-${pad(month + 1)}-${pad(d)}`;
          const entry = entries[dateStr];
          const weekend = (i % 7 === 0) || (i % 7 === 6);
          const isToday = dateStr === todayStr;
          return (
            <button
              key={dateStr}
              type="button"
              title={entry ? `${entry.label}${entry.district ? " · District" : ""}` : dateStr}
              onClick={(e) => onDayClick?.(dateStr, entry, e)}
              data-testid={`cal-day-${dateStr}`}
              className={`h-6 w-full rounded text-[10px] font-medium leading-none transition
                ${entry?.type === "holiday" ? "bg-rose-500 text-white hover:bg-rose-600"
                : entry?.type === "teacher_workday" ? "bg-amber-400 text-slate-900 hover:bg-amber-500"
                : weekend ? "text-slate-300 hover:bg-slate-100"
                : "text-slate-700 hover:bg-slate-100"}
                ${isToday ? "ring-2 ring-primary ring-offset-1" : ""}
                ${onDayClick ? "cursor-pointer active:scale-95" : "cursor-default"}`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export const YearCalendar = ({ year, entries, onDayClick }) => {
  const todayStr = new Date().toISOString().slice(0, 10);
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="year-calendar">
      {Array.from({ length: 12 }, (_, m) => (
        <MonthGrid key={m} year={year} month={m} entries={entries} onDayClick={onDayClick} todayStr={todayStr} />
      ))}
    </div>
  );
};

export const CalendarLegend = () => (
  <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600" data-testid="calendar-legend">
    <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-rose-500" /> Holiday for all</span>
    <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-amber-400" /> Teachers work day</span>
    <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded ring-2 ring-primary ring-offset-1 bg-white" /> Today</span>
  </div>
);

// Chips summarizing custom-named breaks (e.g. "Thanksgiving Break · Nov 23 – 27")
const DEFAULT_LABELS = new Set(["Holiday", "Teachers Work Day"]);
const fmtDay = (d) => new Date(d + "T12:00:00").toLocaleDateString([], { month: "short", day: "numeric" });
const nextDay = (d) => {
  const dt = new Date(d + "T12:00:00");
  dt.setDate(dt.getDate() + 1);
  return dt.toISOString().slice(0, 10);
};

export const NamedBreaks = ({ entries }) => {
  const named = entries
    .filter((e) => e.label && !DEFAULT_LABELS.has(e.label))
    .sort((a, b) => a.date.localeCompare(b.date));
  if (named.length === 0) return null;
  const groups = [];
  for (const e of named) {
    const last = groups[groups.length - 1];
    if (last && last.label === e.label && nextDay(last.end) === e.date) {
      last.end = e.date;
    } else {
      groups.push({ label: e.label, type: e.type, start: e.date, end: e.date });
    }
  }
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="named-breaks">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Named breaks:</span>
      {groups.map((g, i) => (
        <span
          key={i}
          data-testid={`named-break-${i}`}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
            g.type === "teacher_workday" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-700"
          }`}
        >
          {g.label}
          <span className="opacity-70">
            {g.start === g.end ? fmtDay(g.start) : `${fmtDay(g.start)} – ${fmtDay(g.end)}`}
          </span>
        </span>
      ))}
    </div>
  );
};

// Floating menu shown when a day is clicked. menu = {date, entry, x, y}
export const DayMenu = ({ menu, locked, lockedNote, onPick, onClear, onClose }) => {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  if (!menu) return null;
  const left = Math.min(menu.x, window.innerWidth - 250);
  const top = Math.min(menu.y, window.innerHeight - 210);
  const dateLabel = new Date(menu.date + "T12:00:00").toLocaleDateString([], { weekday: "short", month: "long", day: "numeric" });
  return (
    <div className="fixed inset-0 z-50" data-testid="calendar-day-menu-overlay" onClick={onClose}>
      <div
        className="absolute w-60 rounded-xl border border-slate-200 bg-white shadow-2xl p-1.5"
        style={{ left, top }}
        data-testid="calendar-day-menu"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-2.5 pt-1.5 pb-2 flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-900">{dateLabel}</span>
          {menu.entry && (
            <span className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 ${
              menu.entry.type === "holiday" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"
            }`}>{menu.entry.type === "holiday" ? "Holiday" : "Work day"}</span>
          )}
        </div>
        {menu.entry?.label && !["Holiday", "Teachers Work Day"].includes(menu.entry.label) && (
          <div className="px-2.5 pb-2 -mt-1 text-xs font-medium text-slate-600" data-testid="day-menu-label">
            “{menu.entry.label}”
          </div>
        )}
        {locked ? (
          <div className="px-2.5 pb-2 flex items-start gap-2 text-xs text-slate-500" data-testid="calendar-day-locked">
            <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-400" /> {lockedNote}
          </div>
        ) : (
          <>
            <button onClick={() => onPick("holiday")} data-testid="day-menu-holiday"
              className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-slate-800 hover:bg-rose-50 transition">
              <span className="h-7 w-7 rounded-lg bg-rose-100 text-rose-600 inline-flex items-center justify-center"><CalendarOff className="h-4 w-4" /></span>
              Holiday for all
            </button>
            <button onClick={() => onPick("teacher_workday")} data-testid="day-menu-workday"
              className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-slate-800 hover:bg-amber-50 transition">
              <span className="h-7 w-7 rounded-lg bg-amber-100 text-amber-700 inline-flex items-center justify-center"><BriefcaseBusiness className="h-4 w-4" /></span>
              Teachers work day
            </button>
            {menu.entry && (
              <button onClick={onClear} data-testid="day-menu-clear"
                className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-slate-600 hover:bg-slate-100 transition">
                <span className="h-7 w-7 rounded-lg bg-slate-100 text-slate-500 inline-flex items-center justify-center"><X className="h-4 w-4" /></span>
                Clear this day
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
